import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import Chart from 'react-apexcharts';

import { useParos } from './ParosContext';
import { useGeneral } from '../../Context/GeneralContext';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const GraphParos = React.memo(function GraphParos() {
    const { setOpenP, setFolio, statusLinea } = useGeneral();
    const { setSeleccionFila, maxX, setMaxX, minX, setMinX } = useParos();

    const [dataP, setDataP] = useState([]);
    const [userRange, setUserRange] = useState(null);
    const chartParosRef = useRef(null);
    const prevDataPRef = useRef(null);
    const prevMinXRef = useRef(null);
    const prevMaxXRef = useRef(null);
    const userRangeRef = useRef(null);

    const [seleccionFilaB, setSeleccionFilaB] = useState();
    const [chartHeight, setChartHeight] = useState(150);

    useEffect(() => {
        const updateHeight = () => {
            setChartHeight(window.innerHeight < 800 ? 160 : 150);
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const handleChartClick = useCallback((event, chartContext, config) => {
        if (config.seriesIndex !== -1) {
            const idParo = config.config.series[config.seriesIndex].data[0].z[2];
            const estCelda = config.config.series[config.seriesIndex].data[0].z[0];
            if (idParo === seleccionFilaB) {
                setSeleccionFila([]);
                setSeleccionFilaB();
            } else {
                setSeleccionFila([idParo]);
                setSeleccionFilaB(idParo);
                if (estCelda === 6) {
                    setFolio(idParo);
                    setOpenP(true);
                }
            }
        }
    }, [seleccionFilaB, setSeleccionFila, setFolio, setOpenP]);

    const applyUserRange = useCallback((min, max) => {
        if (min === null || max === null || min === undefined || max === undefined) {
            return;
        }
        const nextRange = { min, max };
        userRangeRef.current = nextRange;
        setUserRange(nextRange);
    }, []);

    const clearUserRange = useCallback(() => {
        userRangeRef.current = null;
        setUserRange(null);
    }, []);
    
    const options = useMemo(() => ({
        chart: {
            height: chartHeight,
            type: 'rangeBar',
            redrawOnParentResize: true,
            redrawOnWindowResize: true,
            id: 'rangeBar',
            toolbar: {
                show: false,
            },
            zoom: {
                enabled: true,
                type: 'x',
            },
            selection: {
                enabled: true,
                type: 'x',
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 800
                }
            },
            events: {
                click: handleChartClick,
                selection: (chartContext, { xaxis }) => {
                    applyUserRange(xaxis?.min, xaxis?.max);
                },
                zoomed: (chartContext, { xaxis }) => {
                    applyUserRange(xaxis?.min, xaxis?.max);
                },
                scrolled: (chartContext, { xaxis }) => {
                    applyUserRange(xaxis?.min, xaxis?.max);
                },
                beforeZoom: (chartContext, { xaxis }) => {
                    applyUserRange(xaxis?.min, xaxis?.max);
                    return { xaxis };
                },
                beforeResetZoom: () => {
                    clearUserRange();
                    return { xaxis: { min: minX, max: maxX } };
                },
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '50%',
                rangeBarGroupRows: true,
            },
        },
        noData: {
            text: 'Sin información para el intervalo seleccionado',
            align: 'center',
            verticalAlign: 'top',
            offsetX: 0,
            offsetY: 0,
            style: {
                fontSize: '25px',
                fontFamily: 'WorkSans',
                fontWeight: 600,
                color: '#000',
            },
        },
        fill: {
            type: 'solid',
        },
        xaxis: {
            type: 'datetime',
            min: userRange?.min ?? minX,
            max: userRange?.max ?? maxX,
            labels: {
                show: true,
                datetimeUTC: false,
            },
        },
        yaxis: {
            labels: {
                show: true,
                formatter: () => '',
                style: {
                    fontSize: '0px',
                },
            },
        },
        grid: {
            show: true,
            borderColor: '#90A4AE',
            strokeDashArray: 0,
            position: 'back',
            xaxis: {
                lines: {
                    show: false,
                },
            },
            yaxis: {
                lines: {
                    show: true,
                },
            },
        },
        tooltip: {
            x: {
                show: true,
                format: 'dd MMM HH:mm',
                formatter: undefined,
            },
            fixed: {
                enabled: true,
                position: 'topLeft',
                offsetX: 450,
                offsetY: -100,
            },
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const fechaIni = new Date(w.config.series[seriesIndex].data[0].y[0]);
                const fechaFin = new Date(w.config.series[seriesIndex].data[0].y[1]);
                const nombre = w.config.series[seriesIndex].name;
                const duracion = w.config.series[seriesIndex].data[0].z[1];
                
                return `
                    <div class="Parostooltip">
                        <div>
                            <h3>${nombre}</h3>
                        </div>
                        <div>
                            <h4>
                                Fecha inicial: ${fechaIni.toLocaleString()}<br>
                                Fecha Final: ${fechaFin.toLocaleString()}<br>
                                Duración: ${duracion}
                            </h4>
                        </div>
                    </div>
                `;
            },
        },
        // responsive: [
        //     {
        //         breakpoint: 1000,
        //         options: {
        //             chart: {
        //                 height: 350,
        //                 width: '70%',
        //             },
        //         },
        //     },
        // ],

        colors: [
            function ({ value, seriesIndex, w }) {
                // if (statusLinea.length > 0) {
                if (w.globals.seriesZ[seriesIndex].length > 0) {
                    if (w.globals.seriesZ[seriesIndex][0][0] === 0) {
                        return '#8A8A8A';
                    } else if (w.globals.seriesZ[seriesIndex][0][0] === 1) {
                        return '#2ccb9e';
                    } else if (w.globals.seriesZ[seriesIndex][0][0] === 2) {
                        return '#ffc800';
                    } else if (w.globals.seriesZ[seriesIndex][0][0] === 3) {
                        return '#FE93C2';
                    } else if (w.globals.seriesZ[seriesIndex][0][0] === 4) {
                        return '#c8afe8';
                    } else if (w.globals.seriesZ[seriesIndex][0][0] === 5) {
                        return '#d3acac';
                    } else {
                        return '#fe424d';
                    }
                } else {
                    return '#fe424d';
                }
            },
        ],
        legend: {
            show: false,
        },
    }), [applyUserRange, clearUserRange, handleChartClick, chartHeight, maxX, minX, userRange]);

    const agregarCeroSiEsNecesario = (valor) => {
        if (valor < 10) {
            return `0${  valor}`;
        } else {
            return `${  valor}`;
        }
    };

    const msToHMS = useCallback((ms) => {
        let seconds = ms / 1000;
        const hours = parseInt(seconds / 3600);
        seconds = seconds % 3600;
        const minutes = parseInt(seconds / 60);
        seconds = seconds % 60;
        return `${agregarCeroSiEsNecesario(hours)}:${agregarCeroSiEsNecesario(
            minutes
        )}:${agregarCeroSiEsNecesario(Math.floor(seconds))}`;
    }, []);

    // Función para normalizar fechas: si no tienen zona horaria, las trata como UTC
    const parseDateAsUTC = useCallback((dateString) => {
        // Si la fecha ya tiene zona horaria (termina con Z, +XX:XX o -XX:XX), usar Date.parse directamente
        if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
            return Date.parse(dateString);
        }
        // Si no tiene zona horaria, agregar 'Z' para tratarla como UTC
        return Date.parse(`${dateString}Z`);
    }, []);
    

    useEffect(() => {
        let data1 = [];
        let fechaIni = 0;
        let fechaFin = 0;
        let diff = 0;
        let min = null;
        let max = null;
        statusLinea.forEach((estatusL) => {
            fechaIni = parseDateAsUTC(estatusL.fechaHoraIni);
            fechaFin = parseDateAsUTC(estatusL.fechaHoraFin);
            diff = msToHMS(fechaFin - fechaIni);
            if (min !== null) {
                if (fechaIni < min) {
                    min = fechaIni;
                }
            } else {
                min = fechaIni;
            }

            if (max !== null) {
                if (fechaFin > max) {
                    max = fechaFin;
                }
            } else {
                max = fechaFin;
            }
            data1.push({
                name: estatusL.nombreEstatus,
                data: [
                    {
                        x: 'Estatus Linea',
                        y: [fechaIni, fechaFin],
                        z: [estatusL.estatus, diff, estatusL.folio],
                    },
                ],
            });
        });
        setDataP(data1);
        setMinX(min);
        setMaxX(max);
    }, [msToHMS, parseDateAsUTC, setMaxX, setMinX, statusLinea]);

    useEffect(() => {
        const effectiveMinX = userRange?.min ?? minX;
        const effectiveMaxX = userRange?.max ?? maxX;

        if (prevDataPRef.current === null) {
            prevDataPRef.current = dataP;
            prevMinXRef.current = effectiveMinX;
            prevMaxXRef.current = effectiveMaxX;
            return;
        }

        const dataChanged = JSON.stringify(prevDataPRef.current) !== JSON.stringify(dataP);
        const minXChanged = prevMinXRef.current !== effectiveMinX;
        const maxXChanged = prevMaxXRef.current !== effectiveMaxX;

        if (!dataChanged && !minXChanged && !maxXChanged) {
            return;
        }

        prevDataPRef.current = dataP;
        prevMinXRef.current = effectiveMinX;
        prevMaxXRef.current = effectiveMaxX;

        const timeoutId = setTimeout(() => {
            if (chartParosRef.current && chartParosRef.current.chart) {
                try {
                    if (dataChanged) {
                        chartParosRef.current.chart.updateSeries(dataP, true);
                    }
                    if ((minXChanged || maxXChanged) && effectiveMinX !== null && effectiveMaxX !== null) {
                        chartParosRef.current.chart.updateOptions({
                            xaxis: {
                                min: effectiveMinX,
                                max: effectiveMaxX
                            }
                        }, false, true);
                    }
                } catch (error) {
                    // Silently handle errors
                }
            }
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [dataP, maxX, minX, userRange]);



    return (
        <div className="parosView-body-tittle-second">
            <div className="parosView-body-tittle-second-lists">
                <div className="parosView-body-tittle-second-lists-list0">
                    <FiberManualRecordIcon className="parosView-body-tittle-second-list0" />
                    <h4>Fuera de operación</h4>
                </div>
                <div className="parosView-body-tittle-second-lists-list1">
                    <FiberManualRecordIcon className="parosView-body-tittle-second-list1" />
                    <h4>Operando</h4>
                </div>
                <div className="parosView-body-tittle-second-lists-list2">
                    <FiberManualRecordIcon className="parosView-body-tittle-second-list2" />
                    <h4>Setup</h4>
                </div>
                <div className="parosView-body-tittle-second-lists-list5">
                    <FiberManualRecordIcon className="parosView-body-tittle-second-list5" />
                    <h4>Paro Planeado</h4>
                </div>
                <div className="parosView-body-tittle-second-lists-list6">
                    <FiberManualRecordIcon className="parosView-body-tittle-second-list6" />
                    <h4>Paro no Planeado</h4>
                </div>
            </div>
            <div className="parosView-chart-wrapper">
                <Chart
                    ref={chartParosRef}
                    key="paros-chart"
                    options={options}
                    series={dataP}
                    type="rangeBar"
                    id="rangeBar"
                    height={chartHeight}
                    width={'100%'}
                />
            </div>
        </div>
    );
});

export default GraphParos;
